import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Badge,
  Box,
  Button,
  Container,
  Group,
  Loader,
  Pagination,
  Paper,
  Progress,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  BookOpen,
  Calendar,
  CheckCircle2,
  CircleDot,
  Flame,
  ListChecks,
  Play,
  RefreshCw,
  Video,
  Users,
} from 'lucide-react'
import api from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { TaskMgr } from '../plans/TaskMgr'

/* ------------------------------------------------------------------ */
/*  Types                                                               */
/* ------------------------------------------------------------------ */
interface AssignmentInfo {
  id: number
  uuid?: string
  title: string
  action: string
  assignment_type: string
  route?: string | null
  action_params?: string | null
  status?: string | null
  create_datetime?: string | null
}

interface StudentAssignment {
  assignment: AssignmentInfo
  status: string
  score: number | null
  xp_earned: number
  tutor_feedback: string | null
}

interface MyPlanItem {
  id: number
  status: string
  name: string
}

interface PlanSummary {
  plan_id: number | null
  plan_name: string | null
  assigned_task: number
  status_counts: Record<string, number>
  total_task: number
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */
const STATUS_COLOR: Record<string, string> = {
  assigned: 'blue',
  processing: 'grape',
  graded: 'green',
  reviewed: 'green',
  Completed: 'green',
}

function isReviewedStatus(status: string) {
  return status === 'graded' || status === 'reviewed'
}

const ACTION_ICON: Record<string, React.ReactNode> = {
  practice: <BookOpen size={15} />,
  evaluate: <ListChecks size={15} />,
  mock: <ListChecks size={15} />,
  quiz: <CircleDot size={15} />,
  test: <ListChecks size={15} />,
  review: <RefreshCw size={15} />,
  enhance: <Flame size={15} />,
  video: <Video size={15} />,
  watch: <Play size={15} />,
  meeting: <Users size={15} />,
}

const ACTION_COLOR: Record<string, string> = {
  practice: 'blue',
  evaluate: 'grapefruit',
  mock: 'grapefruit',
  quiz: 'indigo',
  test: 'violet',
  review: 'grape',
  enhance: 'orange',
  video: 'cyan',
  watch: 'blue',
  meeting: 'teal',
}

const PER_PAGE = 10

function buildRoute(sa: StudentAssignment): string | null {
  const a = sa.assignment
  // watch — learning material viewer
  if (a.action === 'watch' && a.route && a.action_params) {
    const base = a.route.replace(/\/$/, '')
    return `${base}/${a.action_params}`
  }
  // review — route to quiz review page
  if (a.action === 'review') {
    return `/student/review/${a.id}`
  }
  // quiz / test / enhance — completed → result page; otherwise → evaluation page
  if (['quiz', 'test', 'enhance'].includes(a.action)) {
    if ((isReviewedStatus(sa.status) || sa.status === 'submitted') && a.route && a.action_params) {
      return `${a.route}/${a.action_params}`
    }
    if (a.uuid) return `/student/evaluate/${a.uuid}`
  }
  if (!a.route) return null
  if (a.route === '/student/evaluate' && a.uuid) {
    return `${a.route}/${a.uuid}/${a.action_params ?? ''}`
  }
  if (a.action_params) {
    return `${a.route}/${a.action_params}`
  }
  // For learning-type assignments with a course route, append assignment_id as query param
  if (a.assignment_type === 'learning' && a.route.startsWith('/courses/')) {
    return `${a.route}?assignmentId=${a.id}`
  }
  return a.route
}


/* ------------------------------------------------------------------ */
/*  Assignment row                                                      */
/* ------------------------------------------------------------------ */
function AssignmentRow({ sa }: { sa: StudentAssignment }) {
  const navigate = useNavigate()
  const a = sa.assignment
  const isDone = isReviewedStatus(sa.status) || sa.status === 'Completed'
  const icon = ACTION_ICON[a.action] || <CircleDot size={15} />
  const color = ACTION_COLOR[a.action] || 'gray'

  function handleClick() {
    const route = buildRoute(sa)
    if (route) navigate(route)
  }

  return (
    <Paper
      radius="md"
      p="sm"
      style={{
        borderLeft: `3px solid var(--mantine-color-${color}-5, var(--sat-green))`,
        background: isDone ? 'var(--sat-green-soft)' : 'var(--sat-surface-dim)',
        opacity: isDone ? 0.75 : 1,
        cursor: 'pointer',
      }}
      onClick={handleClick}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <ThemeIcon size={30} radius="xl" variant="light" color={isDone ? 'green' : color}>
            {isDone ? <CheckCircle2 size={15} /> : icon}
          </ThemeIcon>
          <Box style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} c="var(--sat-text)" truncate>{a.title}</Text>
            {a.create_datetime && (
              <Text size="xs" c="var(--sat-text-muted)">
                {new Date(a.create_datetime).toLocaleDateString()}
              </Text>
            )}
          </Box>
        </Group>
        <Group gap={6} wrap="nowrap">
          <Badge size="sm" variant="light" color={STATUS_COLOR[sa.status] || STATUS_COLOR[a.status || ''] || 'gray'}>
            {(a.status === 'Completed' ? 'completed' : sa.status).replace('_', ' ')}
          </Badge>
          {sa.score != null && (
            <Badge size="sm" variant="light" color="green">{sa.score}%</Badge>
          )}
          <Button size="compact-xs" variant="light" color={color} radius="xl" onClick={(e) => { e.stopPropagation(); handleClick() }}>
            {isDone ? 'Details' : 'Go'}
          </Button>
        </Group>
      </Group>
    </Paper>
  )
}

/* ------------------------------------------------------------------ */
/*  Paginated list                                                      */
/* ------------------------------------------------------------------ */
function AssignmentTabPanel({ items }: { items: StudentAssignment[] }) {
  const [page, setPage] = useState(1)
  const totalPages = Math.max(1, Math.ceil(items.length / PER_PAGE))
  const paged = items.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  if (items.length === 0) {
    return (
      <Text c="var(--sat-text-muted)" ta="center" py="xl" size="sm">
        No assignments in this category.
      </Text>
    )
  }

  return (
    <Stack gap="sm">
      {paged.map((sa) => <AssignmentRow key={sa.assignment.id} sa={sa} />)}
      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={totalPages} value={page} onChange={setPage} radius="xl" color="grapefruit" withEdges />
        </Group>
      )}
    </Stack>
  )
}

/* ------------------------------------------------------------------ */
/*  Main page                                                           */
/* ------------------------------------------------------------------ */
export function StudyPlan() {
  const { user } = useAuth()
  const [assignments, setAssignments] = useState<StudentAssignment[]>([])
  const [myPlans, setMyPlans] = useState<MyPlanItem[]>([])
  const [summary, setSummary] = useState<PlanSummary | null>(null)
  const [loading, setLoading] = useState(true)

  const [typeFilter, setTypeFilter] = useState('all')
  const [taskMgrOpen, setTaskMgrOpen] = useState(false)

  const primaryPlanId = useMemo(() => {
    if (myPlans.length === 0) return null
    const active = myPlans.find((p) => p.status === 'active')
    return (active ?? myPlans[0]).id
  }, [myPlans])

  useEffect(() => {
    setLoading(true)
    Promise.all([
      api.get<StudentAssignment[]>('/assignments'),
      api.get<MyPlanItem[]>('/plans/student'),
      api.get<PlanSummary>('/plans/student/summary'),
    ])
      .then(([assignRes, planRes, summaryRes]) => {
        setAssignments(assignRes.data)
        setMyPlans(planRes.data)
        setSummary(summaryRes.data)
      })
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to load study plan', color: 'red' }))
      .finally(() => setLoading(false))
  }, [])

  function openTaskMgr() {
    if (primaryPlanId == null) {
      notifications.show({
        title: 'No study plan',
        message: 'Ask your tutor to assign a study plan before you can add tasks.',
        color: 'orange',
      })
      return
    }
    setTaskMgrOpen(true)
  }

  async function reloadAssignments() {
    try {
      const res = await api.get<StudentAssignment[]>('/assignments')
      setAssignments(res.data)
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to refresh assignments', color: 'red' })
    }
  }

  const createTaskButton = (
    <Tooltip
      label={primaryPlanId == null ? 'You need a study plan from your tutor first' : 'Add assignment, mock, or learning'}
      withArrow
    >
      <Button
        color="grapefruit"
        radius="xl"
        onClick={openTaskMgr}
        disabled={loading}
      >
        Create New Task
      </Button>
    </Tooltip>
  )

  const filtered = typeFilter === 'all'
    ? assignments
    : assignments.filter((sa) => sa.assignment.action === typeFilter)

  const assigned   = filtered.filter((sa) => sa.status === 'assigned')
  const processing = filtered.filter((sa) => sa.status === 'processing')
  const completed  = filtered.filter((sa) => isReviewedStatus(sa.status) || sa.assignment.status === 'Completed')

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Loader color="eveningSea" />
      </Box>
    )
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Box>
            <Title order={2} c="var(--sat-text)">Study Plan</Title>
            <Text size="sm" c="var(--sat-text-muted)">Track your learning journey across all assigned plans.</Text>
          </Box>
          {createTaskButton}
        </Group>

        {/* ---- Plan Summary ---- */}
        {summary && summary.plan_id != null && (
          <Paper radius="xl" p="md" withBorder style={{ background: 'var(--sat-surface)', borderColor: 'var(--sat-border)' }}>
            <Group justify="space-between" align="center" mb="sm">
              <Text fw={700} size="sm" c="var(--sat-text)">
                {summary.plan_name ?? 'Study Plan'} — Summary
              </Text>
              <Text size="xs" c="var(--sat-text-muted)">
                {summary.assigned_task} / {summary.total_task} tasks
              </Text>
            </Group>

            <Progress
              value={summary.total_task > 0 ? (summary.assigned_task / summary.total_task) * 100 : 0}
              radius="xl"
              size="sm"
              color="grapefruit"
              mb="sm"
            />

            <SimpleGrid cols={3} spacing="xs">
              <Paper radius="md" p="xs" style={{ background: 'var(--sat-surface-dim)', textAlign: 'center' }}>
                <Text fw={700} size="lg" c="var(--sat-text)">{summary.assigned_task}</Text>
                <Text size="xs" c="var(--sat-text-muted)">Tasks in your plate</Text>
              </Paper>
              <Paper radius="md" p="xs" style={{ background: 'var(--sat-surface-dim)', textAlign: 'center' }}>
                <Text fw={700} size="lg" c="var(--sat-text)">{summary.total_task}</Text>
                <Text size="xs" c="var(--sat-text-muted)">Total Task#</Text>
              </Paper>
              <Paper radius="md" p="xs" style={{ background: 'var(--sat-surface-dim)', textAlign: 'center' }}>
                <Text fw={700} size="lg" c="var(--sat-text)">
                  {summary.total_task > 0 ? Math.round((summary.assigned_task / summary.total_task) * 100) : 0}%
                </Text>
                <Text size="xs" c="var(--sat-text-muted)">Progress</Text>
              </Paper>
            </SimpleGrid>

            {Object.keys(summary.status_counts).length > 0 && (
              <Group gap="xs" mt="sm" justify="center">
                {Object.entries(summary.status_counts).map(([status, count]) => (
                  <Badge key={status} size="sm" variant="light" color={STATUS_COLOR[status] || 'gray'}>
                    {status}: {count}
                  </Badge>
                ))}
              </Group>
            )}
          </Paper>
        )}

        <SegmentedControl
          value={typeFilter}
          onChange={setTypeFilter}
          radius="xl"
          data={[
            { label: 'All', value: 'all' },
            { label: 'Learning', value: 'learning' },
            { label: 'Material', value: 'watch' },
            { label: 'Quiz', value: 'quiz' },
            { label: 'Mock', value: 'mock' },
            { label: 'Review', value: 'review' },
            { label: 'Enhance', value: 'enhance' },
          ]}
        />

        {assignments.length === 0 ? (
          <Paper radius="xl" p="xl" withBorder style={{ textAlign: 'center', background: 'var(--sat-surface)', borderColor: 'var(--sat-border)' }}>
            <Calendar size={48} color="var(--sat-blush)" style={{ margin: '0 auto 12px' }} />
            <Text fw={600} c="var(--sat-text)" mb={4}>No assignments yet</Text>
            <Text size="sm" c="var(--sat-text-muted)">
              Your tutor will assign practices and mocks to help you prepare for the SAT — or use Create New Task to add your own.
            </Text>
          </Paper>
        ) : (
          <Tabs defaultValue="assigned" radius="md">
            <Tabs.List>
              <Tabs.Tab value="assigned">
                <Group gap={6} wrap="nowrap">
                  <span>Assigned</span>
                  {assigned.length > 0 && (
                    <Badge size="md" variant="filled" color="blue" circle>{assigned.length}</Badge>
                  )}
                </Group>
              </Tabs.Tab>
              <Tabs.Tab value="processing">
                <Group gap={6} wrap="nowrap">
                  <span>Processing</span>
                  {processing.length > 0 && (
                    <Badge size="md" variant="filled" color="grape" circle>{processing.length}</Badge>
                  )}
                </Group>
              </Tabs.Tab>
              <Tabs.Tab value="completed">
                <Group gap={6} wrap="nowrap">
                  <span>Completed</span>
                  {completed.length > 0 && (
                    <Badge size="md" variant="filled" color="green" circle>{completed.length}</Badge>
                  )}
                </Group>
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="assigned" pt="md">
              <AssignmentTabPanel items={assigned} />
            </Tabs.Panel>
            <Tabs.Panel value="processing" pt="md">
              <AssignmentTabPanel items={processing} />
            </Tabs.Panel>
            <Tabs.Panel value="completed" pt="md">
              <AssignmentTabPanel items={completed} />
            </Tabs.Panel>
          </Tabs>
        )}
      </Stack>

      {primaryPlanId != null && user && (
        <TaskMgr
          opened={taskMgrOpen}
          onClose={() => setTaskMgrOpen(false)}
          plan_id={primaryPlanId}
          insert_position={null}
          target_student_id={user.id}
          current_user_id={user.id}
          role={user.role}
          studentSelfServe
          onPlanSessionsUpdated={reloadAssignments}
        />
      )}
    </Container>
  )
}
